<template>
  <div class="checkbox" :class="{ 'with-label': !!label, disabled: disabled }">
    <input
      ref="input"
      type="checkbox"
      :checked="value"
      @change="handleChange"
      :id="id"
      :disabled="disabled"
      @focus="isFocused = true"
      @blur="isFocused = false"
      @mouseover="hovered = true"
      @mouseleave="hovered = false"
    />
    <div
      class="checkbox-radio"
      :class="{
        checked: value,
        focused: isFocused && !value,
        hovered: hovered && !value && !isFocused
      }"
      @click="inputRef.click()"
    >
      <n-icon class="icon" v-show="value" icon="check" size="xs" strokeWidth="thicker" />
    </div>
    <label :for="id" class="checkbox-label">{{ label }}</label>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import { uuid } from 'vue-uuid'

@Component({
  model: { prop: 'value', event: 'change' }
})
export default class NCheckbox extends Vue {
  @Prop({ type: Boolean }) value!: boolean
  @Prop({ type: String, default: '' }) label!: String
  @Prop({ type: Boolean, default: false }) disabled!: boolean

  id = uuid.v4()
  isFocused = false
  hovered = false

  @Ref('input') inputRef!: HTMLInputElement

  handleChange(event: Event) {
    const target = event.target as HTMLInputElement
    this.$emit('change', target.checked)
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/core/_colors.scss' as *;
@use '@/assets/styles/core/_typography.scss' as *;
@use '@/assets/styles/core/_spacing.scss' as *;
@use '@/assets/styles/core/_radius.scss' as *;

.checkbox {
  position: relative;
  display: flex;
  align-items: center;

  input {
    position: absolute;
    inset: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;
  }

  &:hover {
    .checkbox-radio {
      border-color: $notch-color-neutral-sand-400;
    }
  }

  &.with-label {
    .checkbox-radio {
      margin-right: $notch-space-3x;
    }
  }

  &.disabled {
    cursor: not-allowed;

    .checkbox-radio {
      color: $notch-color-neutral-sand-200 !important;
      border-color: $notch-color-neutral-sand-200 !important;
      border-width: 2px !important;
    }
  }
}

.checkbox-radio {
  width: $notch-space-4x;
  height: $notch-space-4x;
  border-radius: $notch-radius-1x;
  background-color: $notch-color-neutral-grey-000;
  border: 2px solid $notch-color-neutral-sand-200;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, color 0.2s, border-width 0.3s ease-out;
  cursor: pointer;

  &.checked {
    background-color: $notch-color-neutral-grey-900;
    border-color: $notch-color-neutral-grey-900;
    color: $notch-color-neutral-grey-000;
    // keep the dark fill in the PDF export; without it the white tick vanishes on the white page
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  &.focused {
    box-shadow: 0px 0px 0px 2px $notch-color-semantic-accent-500;
  }
}

.checkbox-label {
  @include notch-text-label-light-M;
  flex: 1;
  cursor: pointer;
}
</style>
